// Navigation preferences only: no catalog entries, audio, account data or drafts.
type Mode = 'all' | 'online' | 'local';
type Page = 'library' | 'explore';
type PageState = Record<string, string | boolean>;
type ModeState = { route?: string; scroll: Record<string, number>; library: PageState; explore: PageState; feeds: Record<string, number>; artists: Record<string, 'songs' | 'albums'> };
type StoragePort = Pick<Storage, 'getItem' | 'setItem'>;
export const workspaceViewKey = 'simple-music.workspace-views.v1';
const modes: Mode[] = ['all', 'online', 'local'];
const options: Record<string, readonly string[]> = {
  albumView: ['grid', 'table'], sortOrder: ['recent', 'title', 'artist'], sortDirection: ['asc', 'desc'],
  playHistoryMode: ['week', 'all'], playlistFilter: ['all', 'mine', 'liked'], cloudProvider: ['webdav', 'netease'],
  libraryTab: ['songs', 'albums', 'artists', 'playlists', 'playHistory', 'folders', 'manage', 'recentAdded', 'cloudDisk'],
  librarySource: ['all', 'local', 'webdav', 'online', 'tx', 'wy'],
};
const strings = new Set(['listQuery', 'selectedAlbumId', 'selectedFolder']);
const queryKeys = new Set(['tab', 'source', 'artist', 'playlist', 'onlineTab', 'category', 'cat', 'hq', 'area', 'platform']);
const browsePaths = new Set(['/', '/library', '/explore', '/cloud', '/new-album', '/daily/songs']);
const settingsSections = new Set(['appearance', 'playback', 'lyrics', 'library', 'shortcuts', 'webdav']);
function empty(): ModeState { return { scroll: {}, library: {}, explore: {}, feeds: {}, artists: {} }; }
function record(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}; }

export function workspaceRouteKey(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('/') || value.startsWith('//')) return null;
  try {
    const url = new URL(value, 'https://workspace.invalid');
    if (url.origin !== 'https://workspace.invalid' || (!browsePaths.has(url.pathname) && url.pathname !== '/settings' && !/^\/(playlist|album|artist)\/[^/]+$/.test(url.pathname))) return null;
    if (/account-view|account%2dview/i.test(decodeURIComponent(url.pathname))) return null;
    const query = new URLSearchParams();
    for (const [key, val] of url.searchParams) {
      // Reject rather than persist a credential-bearing or unknown URL.
      if (url.pathname === '/settings') {
        if (key === 'connection') { if (url.searchParams.get('section') !== 'webdav' || !/^[A-Za-z0-9:_-]{1,128}$/.test(val)) return null; }
        else if (key !== 'section' || !settingsSections.has(val)) return null;
      } else if (!queryKeys.has(key) || val.length > 256 || /account-view/i.test(val)) return null;
      query.set(key, val);
    }
    query.sort();
    return url.pathname + (query.size ? '?' + query : '');
  } catch { return null; }
}
export function isWorkspaceBrowseRoute(value: unknown): boolean {
  const route = workspaceRouteKey(value);
  return route !== null && browsePaths.has(route.split('?')[0]);
}
function pageState(page: Page, input: unknown): PageState {
  const result: PageState = {};
  for (const [key, value] of Object.entries(record(input))) {
    if (page === 'explore') { if (key === 'platform' && (value === 'wy' || value === 'tx')) result.platform = value; }
    else if (key === 'favoritesOnly' && typeof value === 'boolean') result[key] = value;
    else if (typeof value === 'string' && (strings.has(key) && value.length <= 512 || Object.hasOwn(options, key) && options[key].includes(value))) result[key] = value;
  }
  return result;
}
export function createWorkspaceViewStore(storage: () => StoragePort | undefined) {
  let state: Record<Mode, ModeState> | undefined;
  function read() {
    if (state) return state;
    let input: Record<string, unknown> = {};
    try { input = record(JSON.parse(storage()?.getItem(workspaceViewKey) || '{}')); } catch { /* Keep session navigation usable. */ }
    state = { all: empty(), online: empty(), local: empty() };
    for (const mode of modes) {
      const saved = record(input[mode]);
      const route = workspaceRouteKey(saved.route);
      if (route && isWorkspaceBrowseRoute(route)) state[mode].route = route;
      for (const [key, value] of Object.entries(record(saved.scroll)).slice(-40)) {
        const safe = workspaceRouteKey(key);
        if (safe && typeof value === 'number' && Number.isFinite(value) && value >= 0) state[mode].scroll[safe] = Math.min(value, 10_000_000);
      }
      state[mode].library = pageState('library', saved.library);
      state[mode].explore = pageState('explore', saved.explore);
      for (const [key, value] of Object.entries(record(saved.feeds)).slice(-20)) {
        if (key.length <= 512 && key.startsWith('SPlayerDiscover') && typeof value === 'number' && Number.isInteger(value) && value >= 1) state[mode].feeds[key] = Math.min(value, 10);
      }
      for (const [key, value] of Object.entries(record(saved.artists)).slice(-40)) {
        const safe = workspaceRouteKey(key);
        if (safe?.startsWith('/artist/') && (value === 'songs' || value === 'albums')) state[mode].artists[safe] = value;
      }
    }
    return state;
  }
  function save() { try { storage()?.setItem(workspaceViewKey, JSON.stringify(read())); } catch { /* Fall back to this session, without changing library data. */ } }
  return {
    route(mode: Mode) { return read()[mode].route; },
    remember(mode: Mode, route: string, scroll?: number) {
      const key = workspaceRouteKey(route);
      if (!key || !modes.includes(mode)) return;
      const current = read()[mode];
      if (isWorkspaceBrowseRoute(key)) current.route = key;
      if (typeof scroll === 'number' && Number.isFinite(scroll) && scroll >= 0) {
        delete current.scroll[key]; current.scroll[key] = Math.min(scroll, 10_000_000);
        const keys = Object.keys(current.scroll); if (keys.length > 40) delete current.scroll[keys[0]];
      }
      save();
    },
    scroll(mode: Mode, route: string) { const key = workspaceRouteKey(route); return key ? read()[mode].scroll[key] || 0 : 0; },
    artistSection(mode: Mode, route: string) { const key = workspaceRouteKey(route); return key ? read()[mode].artists[key] || 'songs' : 'songs'; },
    rememberArtistSection(mode: Mode, route: string, section: string) {
      const key = workspaceRouteKey(route);
      if (!key?.startsWith('/artist/') || !modes.includes(mode) || (section !== 'songs' && section !== 'albums')) return;
      const artists = read()[mode].artists;
      delete artists[key]; artists[key] = section;
      const keys = Object.keys(artists); if (keys.length > 40) delete artists[keys[0]];
      save();
    },
    page(mode: Mode, page: Page) { return { ...read()[mode][page] }; },
    patch(mode: Mode, page: Page, patch: unknown) { Object.assign(read()[mode][page], pageState(page, patch)); save(); },
    feed(mode: Mode, key: string) { return read()[mode].feeds[key] || 1; },
    rememberFeed(mode: Mode, key: string, pages: number) {
      if (key.length > 512 || !key.startsWith('SPlayerDiscover') || !Number.isInteger(pages) || pages < 1) return;
      const feeds = read()[mode].feeds;
      delete feeds[key]; feeds[key] = Math.min(pages, 10);
      const keys = Object.keys(feeds); if (keys.length > 20) delete feeds[keys[0]];
      save();
    },
  };
}
const store = createWorkspaceViewStore(() => typeof localStorage === 'undefined' ? undefined : localStorage);
export const readWorkspaceRoute = store.route;
export const rememberWorkspaceRoute = store.remember;
export const readWorkspaceScroll = store.scroll;
export const readWorkspacePageState = store.page;
export const patchWorkspacePageState = store.patch;
export const readWorkspaceFeedPages = store.feed;
export const rememberWorkspaceFeedPages = store.rememberFeed;
export const readWorkspaceArtistSection = store.artistSection;
export const rememberWorkspaceArtistSection = store.rememberArtistSection;
